// @ts-check
// Aba Tarefas (doc 10, 6.4): tarefas ligadas ao registro, agrupadas por status, checkbox
// redondo para concluir e criação com vínculo via taskTarget.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { createLiveRecordList, updateRecordOptimistically, mutationCounterState } from '../../data/record-store.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { defaultAssigneeMemberId } from '../../data/workspace-members.js';
import { isModifiedClick } from '../../lib/record-panel.js';
import { openRecord } from '../side-panel/open-record.js';
import { findOption, getSortedOptions } from '../../lib/field-metadata.js';
import { formatShortDate } from '../../lib/format.js';
import { Link } from '../../app/router.js';
import { Checkbox, EmptyState, Skeleton } from '../../ui/feedback.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Tag } from '../../ui/chip.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { createActivityForRecord, findActivitiesForRecord, getActivityBodyField, getBodyText } from './activity-targets.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

const DONE_STATUS = 'DONE';
const TODO_STATUS = 'TODO';

/**
 * @param {{ objectNameSingular: string, recordId: string }} props
 */
export function TasksTab({ objectNameSingular, recordId }) {
  const taskObject = objectsByNameSingular.value.get('task');
  const [liveList] = useState(() => createLiveRecordList('task'));
  useEffect(() => () => liveList.dispose(), []);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDueAt, setNewDueAt] = useState('');
  const isSubmittingRef = useRef(false);
  const tasks = liveList.records.value;

  const reload = () =>
    findActivitiesForRecord('task', objectNameSingular, recordId)
      .then(({ activities }) => {
        liveList.records.value = activities;
      })
      .catch(showErrorSnackbar)
      .finally(() => setIsLoading(false));

  useEffect(() => {
    reload();
  }, [objectNameSingular, recordId]);

  if (!taskObject) return html`<${EmptyState} icon="IconCheckbox" title=${t('tasks.unavailable')} />`;
  const statusField = taskObject.fields.find((field) => field.name === 'status');
  const dueField = taskObject.fields.find((field) => field.name === 'dueAt');
  const bodyField = getActivityBodyField(taskObject);

  const createTask = async () => {
    const title = newTitle.trim();
    if (isSubmittingRef.current) return;
    if (!title) {
      setIsCreating(false);
      return;
    }
    // Enter e blur chegam juntos ao confirmar; só a primeira chamada cria a tarefa.
    isSubmittingRef.current = true;
    try {
      // já vinculada ao registro, com prazo e com quem criou como responsável (doc 10, 6.2), salvo o suporte
      const assigneeId = defaultAssigneeMemberId();
      const task = await createActivityForRecord('task', objectNameSingular, recordId, {
        title,
        ...(statusField ? { status: TODO_STATUS } : {}),
        ...(dueField && newDueAt ? { dueAt: new Date(newDueAt).toISOString() } : {}),
        ...(assigneeId && taskObject.fields.some((field) => field.name === 'assignee') ? { assigneeId } : {}),
      });
      liveList.records.value = [task, ...liveList.records.value];
      setNewTitle('');
      setNewDueAt('');
      setIsCreating(false);
      showSuccessSnackbar(t('tasks.created'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const toggleDone = (/** @type {any} */ task, /** @type {boolean} */ checked) => {
    if (!statusField) return;
    updateRecordOptimistically(taskObject, task.id, { status: checked ? DONE_STATUS : TODO_STATUS }).catch(() => {});
  };

  const statusOptions = statusField ? getSortedOptions(statusField) : [];
  const groups = statusOptions.length
    ? statusOptions.map((option) => ({ option, tasks: tasks.filter((task) => task.status === option.value) }))
    : [{ option: null, tasks }];
  const tasksWithoutStatus = statusOptions.length ? tasks.filter((task) => !statusOptions.some((option) => option.value === task.status)) : [];
  if (tasksWithoutStatus.length) groups.push({ option: null, tasks: tasksWithoutStatus });
  void mutationCounterState.value;

  const cancelCreate = () => {
    setIsCreating(false);
    setNewTitle('');
    setNewDueAt('');
  };
  /** @param {KeyboardEvent} event */
  const handleCreateKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      createTask();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      cancelCreate();
    }
  };

  const creationForm = isCreating
    ? html`<div class="activity-create-row">
        <span class="activity-create-checkbox"><${Checkbox} checked=${false} shape="rounded" disabled=${true} hoverable=${false} /></span>
        <input
          class="activity-create-input"
          placeholder=${t('tasks.titlePlaceholder')}
          value=${newTitle}
          ref=${(element) => {
            if (element && !element.dataset.focused) {
              element.dataset.focused = 'true';
              element.focus();
            }
          }}
          onInput=${(event) => setNewTitle(event.currentTarget.value)}
          onKeyDown=${handleCreateKeyDown}
        />
        ${dueField
          ? html`<input class="activity-create-due" type="datetime-local" value=${newDueAt} aria-label=${t('recordPage.tasks.dueAt')}
              title=${t('recordPage.tasks.dueAt')} onInput=${(event) => setNewDueAt(event.currentTarget.value)} onKeyDown=${handleCreateKeyDown} />`
          : null}
        <${Button} title=${t('recordPage.tasks.create')} size="small" variant="secondary" disabled=${!newTitle.trim()} onClick=${createTask} />
        <${LightIconButton} icon="IconX" accent="tertiary" ariaLabel=${t('common.cancel')} onClick=${cancelCreate} />
      </div>`
    : null;

  return html`<div class="activity-tab">
    <div class="activity-tab-header">
      <span class="activity-tab-title">${t('tasks.title')}<span class="activity-tab-count">${tasks.length}</span></span>
      <${Button} icon="IconPlus" title=${t('tasks.new')} size="small" variant="secondary" onClick=${() => setIsCreating(true)} />
    </div>
    ${creationForm}
    ${isLoading
      ? html`<div class="activity-list">${[0, 1, 2].map((index) => html`<div key=${index} class="task-row"><${Skeleton} width="50%" height=${14} /></div>`)}</div>`
      : tasks.length === 0 && !isCreating
        ? html`<${EmptyState}
            icon="IconChecklist"
            title=${t('tasks.emptyTitle')}
            subtitle=${t('tasks.emptySubtitle')}
            action=${html`<${Button} variant="secondary" icon="IconPlus" title=${t('tasks.new')} onClick=${() => setIsCreating(true)} />`}
          />`
        : groups
            .filter((group) => group.tasks.length > 0)
            .map(
              (group) => html`<div class="task-group" key=${group.option?.value ?? 'none'}>
                <div class="task-group-title">
                  ${group.option ? html`<${Tag} color=${group.option.color} text=${translateMetadataLabel(group.option.label)} weight="medium" />` : t('tasks.noStatus')}
                  <span class="activity-tab-count">${group.tasks.length}</span>
                </div>
                <div class="activity-list">
                  ${group.tasks.map((task) => {
                    const isDone = task.status === DONE_STATUS;
                    const isOverdue = !isDone && task.dueAt && new Date(task.dueAt) < new Date();
                    const summary = bodyField ? getBodyText(task[bodyField.name]).split('\n').find((line) => line.trim()) : '';
                    return html`<div class="task-row" key=${task.id}>
                      <${Checkbox} checked=${isDone} shape="rounded" ariaLabel=${t('tasks.markDone')} onChange=${(checked) => toggleDone(task, checked)} />
                      <${Link} href=${`/object/task/${task.id}`} className="task-row-title" data-done=${isDone ? 'true' : undefined}
                        onClick=${(/** @type {MouseEvent} */ event) => {
                          // a tarefa abre no painel lateral, como no Twenty; Ctrl/Cmd+clique abre a página em outra aba
                          if (isModifiedClick(event)) return;
                          event.preventDefault();
                          openRecord({ objectNameSingular: 'task', recordId: task.id, openIn: 'SIDE_PANEL', source: /** @type {Element} */ (event.currentTarget) });
                        }}>
                        ${task.title || t('tasks.titlePlaceholder')}
                      <//>
                      ${summary ? html`<span class="task-row-summary">${summary}</span>` : null}
                      <span class="task-row-spacer"></span>
                      ${statusField && !group.option ? html`<span>${translateMetadataLabel(findOption(statusField, task.status)?.label ?? '')}</span>` : null}
                      ${dueField && task.dueAt
                        ? html`<span class="task-row-due" data-overdue=${isOverdue ? 'true' : undefined}>
                            <${Icon} name="IconCalendar" size=${14} />${formatShortDate(task.dueAt)}
                          </span>`
                        : null}
                    </div>`;
                  })}
                </div>
              </div>`,
            )}
  </div>`;
}
